/**
 * Real-time Stream Manager
 *
 * Uses Server-Sent Events (SSE) for real-time delivery of:
 * - Thinking events (AI reasoning/consideration subjects)
 * - Content chunks (response text)
 * - Tool calls (function invocations)
 * - Tool responses (function results)
 * - Stream completion markers
 *
 * This replaces polling with true real-time streaming via Redis pub/sub.
 */

import { useMessageStore } from '../stores/messageStore';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getCurrentUserToken } from './firebase';

interface StreamEventData {
  type: 'connected' | 'stream_event' | 'chunk_recovery' | 'heartbeat' | 'error';
  eventType?: 'thinking' | 'content' | 'tool_call' | 'tool_response' | 'complete' | 'agent_notification' | 'agent_choice_request' | 'scripts-batch-updated';
  data?: any;
  chunkIndex?: number;
  timestamp?: string;
  connectionId?: string;
  message?: string;
  chunk?: any;
}

interface ActiveStream {
  messageId: string;
  conversationId: string;
  eventSource: EventSource;
  lastChunkIndex: number;
  startedAt: number;
  reconnectAttempts: number;
}

interface JobResponse {
  mode: string;
  jobId: string;
  messageId: string;
  conversationId: string;
  status: string;
  pollUrls: {
    events: string;
    status: string;
    chunks: string;
  };
  initialMessage: {
    id: string;
    role: string;
    content: string;
    timestamp: string;
    metadata: any;
  };
  createdAt: string;
}

class RealtimeStreamManager {
  private activeStreams = new Map<string, ActiveStream>();
  private broadcastChannel: BroadcastChannel | null = null;
  private maxReconnectAttempts = 5;
  private reconnectDelayMs = 1000;

  constructor() {
    // Setup BroadcastChannel for multi-tab synchronization
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('realtime-streaming');
      this.broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'stream-started') {
          // Another tab started streaming - we can optionally connect too
          logger.debug('📡 RealtimeStreamManager: Another tab started streaming', event.data);
        } else if (event.data.type === 'stream-stopped') {
          // Another tab stopped streaming
          this.stopStream(event.data.messageId, false);
        }
      };
    }

    // Handle visibility changes for reconnection
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.reconnectActiveStreams();
      }
    });
  }

  /**
   * Start real-time SSE stream for a message
   */
  async startStream(messageId: string, conversationId: string, sinceIndex: number = 0): Promise<void> {
    // Close existing stream for this message if any
    this.stopStream(messageId, false);

    logger.info('📡 RealtimeStreamManager: Starting SSE stream', { messageId, conversationId, sinceIndex });

    try {
      // Get Firebase auth token
      const token = await getCurrentUserToken();

      if (!token) {
        logger.error('❌ RealtimeStreamManager: No auth token available');
        return;
      }

      // Build SSE URL
      const apiBaseUrl = getApiBaseUrl();
      const streamUrl = `${apiBaseUrl}/conversations/${conversationId}/messages/${messageId}/stream-events`;
      const authenticatedUrl = `${streamUrl}?token=${encodeURIComponent(token)}${sinceIndex > 0 ? `&sinceIndex=${sinceIndex}` : ''}`;

      // Create EventSource
      const eventSource = new EventSource(authenticatedUrl);

      // Store stream info
      const streamInfo: ActiveStream = {
        messageId,
        conversationId,
        eventSource,
        lastChunkIndex: sinceIndex,
        startedAt: Date.now(),
        reconnectAttempts: 0,
      };
      this.activeStreams.set(messageId, streamInfo);

      // Handle messages
      eventSource.onmessage = (event) => {
        try {
          const data: StreamEventData = JSON.parse(event.data);
          this.handleStreamEvent(messageId, conversationId, data, streamInfo);
        } catch (error) {
          logger.error('❌ RealtimeStreamManager: Failed to parse SSE event', { error, data: event.data });
        }
      };

      // Handle errors
      eventSource.onerror = (error) => {
        logger.warn('⚠️ RealtimeStreamManager: SSE connection error', { messageId, error });

        // Check if we should reconnect
        if (streamInfo.reconnectAttempts < this.maxReconnectAttempts) {
          streamInfo.reconnectAttempts++;
          const delay = this.reconnectDelayMs * Math.pow(2, streamInfo.reconnectAttempts - 1);

          logger.info('🔄 RealtimeStreamManager: Attempting reconnect', {
            messageId,
            attempt: streamInfo.reconnectAttempts,
            delayMs: delay,
          });

          setTimeout(() => {
            if (this.activeStreams.has(messageId)) {
              this.startStream(messageId, conversationId, streamInfo.lastChunkIndex);
            }
          }, delay);
        } else {
          logger.error('❌ RealtimeStreamManager: Max reconnect attempts reached', { messageId });
          this.stopStream(messageId);
        }
      };

      // Handle open
      eventSource.onopen = () => {
        logger.info('✅ RealtimeStreamManager: SSE connection opened', { messageId });
        streamInfo.reconnectAttempts = 0; // Reset on successful connection
      };

      // Notify other tabs
      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage({
          type: 'stream-started',
          messageId,
          conversationId,
        });
      }

    } catch (error) {
      logger.error('❌ RealtimeStreamManager: Failed to start stream', { messageId, error });
    }
  }

  /**
   * Handle incoming SSE events
   */
  private handleStreamEvent(
    messageId: string,
    conversationId: string,
    data: StreamEventData,
    streamInfo: ActiveStream
  ): void {
    const store = useMessageStore.getState();
    const message = store.getMessageById(messageId);

    if (!message && data.type !== 'connected') {
      logger.warn('⚠️ RealtimeStreamManager: Message not found', { messageId });
      return;
    }

    switch (data.type) {
      case 'connected':
        logger.info('🔗 RealtimeStreamManager: Connected to stream', {
          messageId,
          connectionId: data.connectionId,
        });
        break;

      case 'chunk_recovery':
        // Handle recovered chunks from reconnection
        if (data.chunk) {
          this.processChunk(messageId, data.chunk, streamInfo);
        }
        break;

      case 'stream_event':
        // Handle real-time stream events
        this.processStreamEvent(messageId, conversationId, data, streamInfo);
        break;

      case 'heartbeat':
        // Update last activity time
        logger.debug('💓 RealtimeStreamManager: Heartbeat received', { messageId });
        break;

      case 'error':
        logger.error('❌ RealtimeStreamManager: Stream error', {
          messageId,
          message: data.message,
        });
        if (message) {
          store.updateMessage(messageId, {
            status: 'error',
            metadata: {
              ...message.metadata,
              error: data.message || 'Stream error',
              isStreaming: false,
            },
          });
        }
        this.stopStream(messageId);
        break;
    }
  }

  /**
   * Process a stream event (thinking, content, tool_call, etc.)
   */
  private processStreamEvent(
    messageId: string,
    _conversationId: string,
    data: StreamEventData,
    streamInfo: ActiveStream
  ): void {
    const store = useMessageStore.getState();
    const message = store.getMessageById(messageId);

    if (!message) return;

    // Update last chunk index
    if (data.chunkIndex !== undefined) {
      streamInfo.lastChunkIndex = data.chunkIndex;
    }

    logger.debug('📋 RealtimeStreamManager: Processing event', {
      messageId,
      eventType: data.eventType,
      chunkIndex: data.chunkIndex,
    });

    switch (data.eventType) {
      case 'thinking':
        // Update current thinking subject
        const thoughtData = data.data;
        let subject = thoughtData?.subject || thoughtData?.content || '';

        // Filter invalid subjects
        if (!subject || subject.length > 100 || subject.length < 2) {
          subject = 'Processing';
        }

        store.updateMessage(messageId, {
          currentThinkingSubject: subject,
          currentThinking: thoughtData?.content || subject,
          thinkingHistory: [],
        });
        break;

      case 'content':
        // Append content chunk with deduplication and smart spacing
        // Handle both formats: { text: "..." } (legacy) and { content: "..." } (Claude)
        const contentData = data.data;
        let contentText = contentData?.text || contentData?.content || (typeof contentData === 'string' ? contentData : '');

        // Filter out "think:" prefixed content - this is internal model reasoning
        // that shouldn't be displayed to users
        const isThinkingChunk = contentText && /^think:/i.test(contentText.trim());
        const wasInThinkingMode = message.metadata?.isThinkingContent === true;

        // Enter thinking mode when we see "think:" prefix
        if (isThinkingChunk) {
          logger.debug('📋 RealtimeStreamManager: Filtering out thinking content (start)', {
            messageId,
            preview: contentText.substring(0, 50),
          });
          // Update thinking indicator with this content
          const thinkingText = contentText.replace(/^think:\s*/i, '').trim();
          if (thinkingText) {
            store.updateMessage(messageId, {
              currentThinkingSubject: thinkingText.substring(0, 80),
              metadata: {
                ...message.metadata,
                isThinkingContent: true,
              }
            });
          }
          break;
        }

        // Continue filtering if we're in thinking mode (continuation chunks)
        // Exit thinking mode when we see content that looks like a real response
        // (starts with capital letter, bullet point, or markdown header)
        if (wasInThinkingMode) {
          const looksLikeRealContent = /^[A-Z#\-*•]/.test(contentText.trim());
          if (!looksLikeRealContent) {
            logger.debug('📋 RealtimeStreamManager: Filtering thinking continuation', {
              messageId,
              preview: contentText.substring(0, 50),
            });
            // Update thinking indicator with continuation
            const thinkingText = contentText.trim();
            if (thinkingText) {
              store.updateMessage(messageId, {
                currentThinkingSubject: thinkingText.substring(0, 80),
              });
            }
            break;
          } else {
            // Exit thinking mode - this looks like real content
            logger.debug('📋 RealtimeStreamManager: Exiting thinking mode', {
              messageId,
              preview: contentText.substring(0, 50),
            });
            store.updateMessage(messageId, {
              metadata: {
                ...message.metadata,
                isThinkingContent: false,
              }
            });
          }
        }

        if (contentText) {
          // Skip duplicate chunks based on index
          const lastChunkIndex = message.metadata?.lastChunkIndex || -1;
          if (data.chunkIndex !== undefined && data.chunkIndex <= lastChunkIndex) {
            logger.debug('📋 RealtimeStreamManager: Skipping duplicate content chunk', {
              messageId,
              chunkIndex: data.chunkIndex,
              lastChunkIndex,
            });
            break;
          }

          // Smart spacing: add paragraph break between separate content chunks
          // This handles cases where the model outputs multiple turns without proper spacing
          let textToAppend = contentText;
          if (message.content.length > 0 && contentText.length > 0) {
            const lastChar = message.content.slice(-1);
            const firstChar = contentText[0];
            const needsSpacing = /[.!?]/.test(lastChar) && /[A-Z]/.test(firstChar);
            const needsSpace = /[.!?,;:]/.test(lastChar) && !/\s/.test(firstChar);

            if (needsSpacing) {
              // End of sentence followed by new sentence - add paragraph break
              textToAppend = '\n\n' + contentText;
            } else if (needsSpace) {
              // End of clause followed by content - add space
              textToAppend = ' ' + contentText;
            }
          }

          store.appendToMessage(messageId, textToAppend);

          // Update last chunk index
          if (data.chunkIndex !== undefined) {
            store.updateMessage(messageId, {
              metadata: {
                ...message.metadata,
                lastChunkIndex: data.chunkIndex,
              }
            });
          }
        }
        break;

      case 'tool_call':
        // Update tool call state and populate toolCalls metadata
        const toolCallData = data.data;
        const toolId = toolCallData?.id || `tool-${Date.now()}`;

        // Get current toolCalls array or initialize
        const currentToolCalls = message.metadata?.toolCalls || [];

        // Add new tool call to the array
        store.updateMessage(messageId, {
          currentThinkingSubject: `Calling ${toolCallData?.name || 'tool'}`,
          metadata: {
            ...message.metadata,
            toolCalls: [
              ...currentToolCalls,
              {
                id: toolId,
                name: toolCallData?.name || 'Unknown Tool',
                status: 'pending' as const,
                parameters: toolCallData?.parameters || toolCallData?.args || toolCallData?.input,
                startedAt: new Date().toISOString(),
              }
            ]
          }
        });
        break;

      case 'tool_response':
        // Update the most recent pending tool call with results
        const responseData = data.data;
        const existingToolCalls = message.metadata?.toolCalls || [];

        // Find and update the most recent pending tool call
        const updatedToolCalls = existingToolCalls.map((tc: any, index: number) => {
          if (index === existingToolCalls.length - 1 && tc.status === 'pending') {
            return {
              ...tc,
              status: 'completed' as const,
              result: responseData?.result || responseData?.content || responseData,
              completedAt: new Date().toISOString(),
            };
          }
          return tc;
        });

        store.updateMessage(messageId, {
          currentThinkingSubject: 'Processing tool result',
          metadata: {
            ...message.metadata,
            toolCalls: updatedToolCalls,
          }
        });
        break;

      case 'complete':
        // Stream complete - finalize message with final content from worker
        logger.info('✅ RealtimeStreamManager: Stream complete', {
          messageId,
          totalChunks: data.data?.totalChunks,
          hasFinalContent: !!data.data?.finalContent,
          slidesUpdated: data.data?.slidesUpdated,
          scriptsUpdated: data.data?.scriptsUpdated,
        });

        // Update message with final content (if provided) and mark as completed
        store.updateMessage(messageId, {
          // Use finalContent from the complete event if available
          ...(data.data?.finalContent ? { content: data.data.finalContent } : {}),
          status: 'completed',
          metadata: {
            ...message.metadata,
            streamComplete: true,
            isStreaming: false,
            completedAt: data.timestamp || new Date().toISOString(),
            slidesUpdated: data.data?.slidesUpdated,
            slideUpdateCount: data.data?.slideUpdateCount,
            scriptsUpdated: data.data?.scriptsUpdated,
            scriptUpdateSlideIds: data.data?.scriptUpdateSlideIds,
          },
          currentThinkingSubject: undefined,
          currentThinking: undefined,
          thinkingHistory: [],
        });
        store.setStreamingMessage(null);
        this.stopStream(messageId);

        // Dispatch scripts-batch-updated event for NarrationView if scripts were updated
        if (data.data?.scriptsUpdated && data.data?.scriptUpdateSlideIds?.length > 0) {
          logger.info('📝 RealtimeStreamManager: Dispatching scripts-batch-updated event', {
            messageId,
            conversationId: message.conversationId,
            scriptUpdateSlideIds: data.data.scriptUpdateSlideIds,
          });
          window.dispatchEvent(new CustomEvent('scripts-batch-updated', {
            detail: {
              conversationId: message.conversationId,
              slideIds: data.data.scriptUpdateSlideIds,
              timestamp: data.timestamp || new Date().toISOString(),
              source: 'narration_chat',
            }
          }));
        }

        // Emit completion event for notification system
        window.dispatchEvent(new CustomEvent('aiResponseComplete', {
          detail: { messageId }
        }));
        break;

      case 'agent_notification':
        // Agent sent a notification (progress/warning/error)
        logger.info('📢 RealtimeStreamManager: Agent notification', {
          messageId,
          severity: data.data?.severity,
          message: data.data?.message?.substring(0, 50),
        });

        // Emit event for UI to display notification banner
        window.dispatchEvent(new CustomEvent('agentNotification', {
          detail: {
            messageId,
            message: data.data?.message,
            severity: data.data?.severity,
            context: data.data?.context,
            timestamp: data.timestamp || new Date().toISOString(),
          }
        }));
        break;

      case 'agent_choice_request':
        // Agent needs user input to proceed
        logger.info('❓ RealtimeStreamManager: Agent choice request', {
          messageId,
          requestId: data.data?.requestId,
          question: data.data?.question?.substring(0, 50),
        });

        // Emit event for UI to display choice prompt
        window.dispatchEvent(new CustomEvent('agentChoiceRequest', {
          detail: {
            messageId,
            requestId: data.data?.requestId,
            question: data.data?.question,
            context: data.data?.context,
            options: data.data?.options,
            recommendation: data.data?.recommendation,
            timestamp: data.timestamp || new Date().toISOString(),
          }
        }));
        break;
    }
  }

  /**
   * Process a recovered chunk (from reconnection)
   */
  private processChunk(messageId: string, chunk: any, streamInfo: ActiveStream): void {
    const store = useMessageStore.getState();
    const message = store.getMessageById(messageId);

    if (!message) return;

    // Update last chunk index
    if (chunk.chunkIndex !== undefined) {
      streamInfo.lastChunkIndex = chunk.chunkIndex;
    }

    // Process based on chunk type
    switch (chunk.type) {
      case 'content':
        if (chunk.content) {
          store.appendToMessage(messageId, chunk.content);
        }
        break;
      case 'thinking':
      case 'thought':
        // Skip recovered thinking chunks - only show current
        break;
    }
  }

  /**
   * Stop stream for a message
   */
  stopStream(messageId: string, broadcast = true): void {
    const stream = this.activeStreams.get(messageId);
    if (!stream) return;

    logger.info('🛑 RealtimeStreamManager: Stopping stream', { messageId });

    // Close EventSource
    stream.eventSource.close();
    this.activeStreams.delete(messageId);

    // Notify other tabs
    if (broadcast && this.broadcastChannel) {
      this.broadcastChannel.postMessage({
        type: 'stream-stopped',
        messageId,
      });
    }
  }

  /**
   * Handle message sent - called when user sends a message
   */
  onMessageSent(response: JobResponse): void {
    const { messageId, conversationId } = response;
    logger.info('📤 RealtimeStreamManager: Message sent, starting stream', { messageId, conversationId });
    this.startStream(messageId, conversationId, 0);
  }

  /**
   * Reconnect active streams (called on visibility change)
   */
  private reconnectActiveStreams(): void {
    for (const [messageId, stream] of this.activeStreams.entries()) {
      if (stream.eventSource.readyState === EventSource.CLOSED) {
        logger.info('🔄 RealtimeStreamManager: Reconnecting stream after visibility change', { messageId });
        this.startStream(messageId, stream.conversationId, stream.lastChunkIndex);
      }
    }
  }

  /**
   * Recover incomplete messages on page load
   */
  async recoverIncompleteMessages(): Promise<void> {
    logger.info('🔄 RealtimeStreamManager: Checking for incomplete messages');

    const store = useMessageStore.getState();
    const messagesByConversation = store.messagesByConversation;

    // Find messages that were streaming across all conversations
    for (const conversationId in messagesByConversation) {
      const messages = messagesByConversation[conversationId];
      for (const message of messages) {
        if (message.metadata?.isStreaming && !message.metadata?.streamComplete) {
          logger.info('🔄 RealtimeStreamManager: Found incomplete message, starting stream', {
            messageId: message.id,
            conversationId: message.conversationId,
          });

          // Start stream from last known chunk index
          const lastIndex = message.metadata?.lastChunkIndex || 0;
          await this.startStream(message.id, message.conversationId, lastIndex);
        }
      }
    }
  }

  /**
   * Get status of all active streams
   */
  getStatus(): { streams: Array<{ messageId: string; conversationId: string; lastChunkIndex: number }> } {
    const streams = Array.from(this.activeStreams.values()).map((s) => ({
      messageId: s.messageId,
      conversationId: s.conversationId,
      lastChunkIndex: s.lastChunkIndex,
    }));
    return { streams };
  }

  /**
   * Check if a message has an active stream
   */
  hasActiveStream(messageId: string): boolean {
    return this.activeStreams.has(messageId);
  }
}

// Export singleton instance
export const realtimeStreamManager = new RealtimeStreamManager();
